import React, { createContext, useCallback, useContext, useState } from 'react';
import type { NavItem } from './navConfig';
import { EMPTY_RAIL_VISIBLE } from './railLayout';

const RailVisibleContext = createContext<ReadonlySet<NavItem['id']>>(EMPTY_RAIL_VISIBLE);

const sameIds = (a: ReadonlySet<NavItem['id']>, b: ReadonlySet<NavItem['id']>) =>
    a.size === b.size && [...a].every((id) => b.has(id));

type PublishRail = (ids: ReadonlySet<NavItem['id']>) => void;

const PublishRailContext = createContext<PublishRail>(() => {});

/**
 * What the side rail is showing right now. The header menu hides a destination only when it is in
 * this set, so a link the rail drops cannot disappear from both places. No provider means nothing
 * is covered: the menu keeps every link.
 */
export const RailVisibleProvider: React.FC<React.PropsWithChildren<{ visibleIds?: ReadonlySet<NavItem['id']> }>> = ({
    children,
    visibleIds,
}) => {
    const [published, setPublished] = useState<ReadonlySet<NavItem['id']>>(EMPTY_RAIL_VISIBLE);
    const publish = useCallback<PublishRail>((ids) => {
        setPublished((prev) => (sameIds(prev, ids) ? prev : ids));
    }, []);

    return (
        <PublishRailContext.Provider value={publish}>
            <RailVisibleContext.Provider value={visibleIds ?? published}>{children}</RailVisibleContext.Provider>
        </PublishRailContext.Provider>
    );
};

export const useRailVisibleIds = () => useContext(RailVisibleContext);

export const usePublishRailVisible = () => useContext(PublishRailContext);
